import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Input, Space, Tag, Typography } from 'antd';
import { CloseOutlined, EditOutlined, PlusOutlined, ProfileTwoTone, SearchOutlined } from '@ant-design/icons';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import tileStyles from 'src/styles/tile.module.css';
import { Link, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';
import ConfirmButton from 'src/components/atoms/ConfirmButton';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import { addGroupToTemplate, removeGroupFromTemplate } from 'src/features/templates/templates.state';
import { useTemplateGroupsSectionById } from 'src/features/templates/templates.hooks';
import { ContentTemplateCompany } from 'src/features/templates/templates.types';
import { useDeviceGroups } from 'src/features/deviceGroups/deviceGroups.hooks';
import { fetchDeviceGroups } from 'src/features/deviceGroups/deviceGroups.state';

interface TemplateAddGroupssProps {
  templateId: string;
  currentGroups: ContentTemplateCompany[];
}

const TemplateAddDeviceGroup: React.FC<TemplateAddGroupssProps> = props => {
  const intl = useIntl();
  const dispatch: AppDispatch = useDispatch();
  const groups = useDeviceGroups();
  const [name, setName] = useState<string | undefined>(undefined);
  const currentGroupsIds = useMemo(() => props.currentGroups.map(c => c.id), [props.currentGroups]);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddButtonClick = useCallback(
    async (groupId: string) => {
      setIsLoading(true);
      await dispatch(addGroupToTemplate({ templateId: props.templateId, data: { groupId } }));
      setIsLoading(false);
    },
    [dispatch, props.templateId]
  );

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await dispatch(fetchDeviceGroups({}));
      setIsLoading(false);
    })();
  }, [dispatch]);
  useDebounce(
    () => {
      if (name !== undefined && !isLoading) {
        (async () => {
          setIsLoading(true);
          await dispatch(fetchDeviceGroups({ params: { name } }));
          setIsLoading(false);
        })();
      }
    },
    300,
    [dispatch, name]
  );

  return (
    <>
      <Input
        autoFocus
        allowClear
        placeholder={intl.formatMessage({ id: 'placeholder.SearchVideo' })}
        prefix={<SearchOutlined />}
        onChange={e => {
          setName(e.target.value);
        }}
        style={{ marginBottom: 10 }}
      />
      <div className={styles.content}>
        <LoaderWrapper loading={isLoading}>
          {groups
            .filter(c => !currentGroupsIds.includes(c.id) && !c.disabled)
            .map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/testing/groups/${c.id}/videos`} className={tileStyles.tileName}>
                  <Space>
                    <ProfileTwoTone />
                    {c.name}
                  </Space>
                </Link>
                <Space>
                  {c.disabled && (
                    <Tag color={'orange'}>
                      <FormattedMessage id={'text.archived'} />
                    </Tag>
                  )}
                  <Button type={'primary'} size='small' onClick={() => handleAddButtonClick(c.id)}>
                    <FormattedMessage id={'action.Add'} />
                  </Button>
                </Space>
              </div>
            ))}
        </LoaderWrapper>
      </div>
    </>
  );
};

const ContentTemplateDeviceGroupsTab: React.FC = () => {
  const { templateId } = useParams() as { templateId: string };
  const [addMode, setAddMode] = useState<boolean>(false);
  const [editMode, setEditMode] = useState<boolean>(false);
  const groups = useTemplateGroupsSectionById(templateId);
  const dispatch: AppDispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const handleRemoveButtonClick = useCallback(
    async (groupId: string) => {
      setIsLoading(true);
      await dispatch(removeGroupFromTemplate({ templateId, groupId }));
      setIsLoading(false);
    },
    [dispatch, templateId]
  );

  const handleAddButtonClick = useCallback(() => {
    setAddMode(m => !m);
  }, []);

  const handleEditButtonClick = useCallback(() => {
    setEditMode(m => !m);
  }, []);

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>A/B Группы</Typography.Title>
        <Space direction={'horizontal'}>
          {!editMode && (
            <Button type={addMode ? undefined : 'primary'} onClick={handleAddButtonClick}>
              {addMode ? <CloseOutlined /> : <PlusOutlined />}
            </Button>
          )}
          {!addMode && (
            <Button onClick={handleEditButtonClick}>{editMode ? <CloseOutlined /> : <EditOutlined />}</Button>
          )}
        </Space>
      </div>
      <div className={styles.content}>
        {addMode && <TemplateAddDeviceGroup templateId={templateId} currentGroups={groups} />}
        {!addMode && (
          <LoaderWrapper loading={isLoading}>
            {groups?.map(c => (
              <div key={c.id} className={tileStyles.listTile}>
                <Link to={`/testing/groups/${c.id}`} className={tileStyles.tileName}>
                  <Space>
                    <ProfileTwoTone />
                    {c.name}
                  </Space>
                </Link>
                <Space>
                  {editMode && (
                    <ConfirmButton danger size='small' onClick={() => handleRemoveButtonClick(c.id)}>
                      <FormattedMessage id={'action.Remove'} />
                    </ConfirmButton>
                  )}
                </Space>
              </div>
            ))}
          </LoaderWrapper>
        )}
      </div>
    </div>
  );
};
export default ContentTemplateDeviceGroupsTab;
